import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { MicIcon, SendIcon, StopIcon } from "./Icons";

const SpeechRecognition = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

/** Auto-growing input with send / stop / voice. */
const Composer = forwardRef(function Composer({ value, onChange, onSend, onStop, loading, maxLength, placeholder }, ref) {
  const taRef = useRef(null);
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState("");

  useImperativeHandle(ref, () => ({ focus: () => taRef.current && taRef.current.focus() }));

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, [value]);

  useEffect(() => () => recRef.current && recRef.current.abort(), []);

  const canSend = value.trim().length > 0 && !loading;

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (canSend) onSend();
    }
  };

  const toggleVoice = () => {
    if (!SpeechRecognition) return;
    if (listening) {
      recRef.current && recRef.current.stop();
      return;
    }
    setVoiceError("");
    const rec = new SpeechRecognition();
    rec.lang = "en-IN";
    rec.interimResults = true;
    rec.continuous = false;
    const base = value ? `${value.trim()} ` : "";
    rec.onresult = (e) => {
      let text = "";
      for (let i = 0; i < e.results.length; i++) text += e.results[i][0].transcript;
      onChange((base + text).slice(0, maxLength));
    };
    rec.onerror = (e) => {
      setVoiceError(e.error === "not-allowed" ? "Microphone permission denied." : "Voice input failed. Try again.");
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
      taRef.current && taRef.current.focus();
    };
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (err) {
      setListening(false);
    }
  };

  const nearLimit = value.length > maxLength * 0.85;

  return (
    <div className="bdai-composer">
      <div className={`bdai-composer-box ${listening ? "bdai-listening" : ""}`}>
        <textarea
          ref={taRef}
          className="bdai-textarea"
          rows={1}
          value={value}
          maxLength={maxLength}
          placeholder={listening ? "Listening…" : placeholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKey}
          aria-label="Ask a question"
        />
        <div className="bdai-composer-btns">
          {SpeechRecognition && !loading && (
            <button
              type="button"
              className={`bdai-icon-btn ${listening ? "bdai-mic-on" : ""}`}
              onClick={toggleVoice}
              title={listening ? "Stop listening" : "Speak your question"}
              aria-label={listening ? "Stop listening" : "Voice input"}
              aria-pressed={listening}
            >
              <MicIcon size={17} />
            </button>
          )}
          {loading ? (
            <button type="button" className="bdai-send bdai-send-stop" onClick={onStop} title="Stop" aria-label="Stop generating">
              <StopIcon size={14} />
            </button>
          ) : (
            <button type="button" className="bdai-send" onClick={onSend} disabled={!canSend} title="Send (Enter)" aria-label="Send">
              <SendIcon size={16} />
            </button>
          )}
        </div>
      </div>
      <div className="bdai-composer-hint">
        {voiceError ? (
          <span className="bdai-text-danger">{voiceError}</span>
        ) : (
          <span>
            <kbd>Enter</kbd> to send · <kbd>Shift</kbd>+<kbd>Enter</kbd> new line
          </span>
        )}
        {nearLimit && <span className={value.length >= maxLength ? "bdai-text-danger" : ""}>{value.length}/{maxLength}</span>}
      </div>
    </div>
  );
});

export default Composer;
